import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import type { MePublic } from "../lib/types";

interface AuthCtx {
  me: MePublic | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, refetch } = useQuery<MePublic | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<MePublic>("/api/auth/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });

  const value = useMemo<AuthCtx>(
    () => ({
      me: data ?? null,
      isLoading,
      refetch: async () => {
        await refetch();
      },
    }),
    [data, isLoading, refetch],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside AuthProvider");
  return v;
}
